/**
 * Read-only sample capacity for the email-only workspace.
 *
 * A new user needs to see what DDWS rents before being asked for a wallet, and
 * the only honest way to show that without an address is sample content. The
 * risk is obvious — sample content that reads as *your* content is a lie — so
 * the isolation is built into the type rather than left to the components:
 *
 * - `kind: 'example'` is a required literal, so an `ExampleMachine` can never be
 *   passed where an `OverviewInstanceSummary` is expected, and vice versa;
 * - there is no `dseq`, no owner, no address, and deliberately **no price,
 *   balance, runway, or status** — nothing a tenant could mistake for their own
 *   account, and nothing that would need a chain read to be true;
 * - nothing here is derived from, or feeds into, a wallet-scoped query. The
 *   values are constants, not a snapshot of anybody's capacity.
 *
 * What each row *does* claim is only what a shape is: cores, memory, disk. Those
 * are the product's own tiers, not a report about the network, so they stay true
 * with no chain behind them.
 */

/** A specification cell: the value a tenant reads, and what it measures. */
export interface ExampleSpec {
  value: string
  label: string
}

export interface ExampleMachine {
  /** Present on every sample row, absent from every real one. Not decorative. */
  readonly kind: 'example'
  id: string
  name: string
  summary: string
  /** Which token pair tints the row mark. Kept to the shared palette. */
  tone: 'example' | 'positive' | 'info'
  specs: [ExampleSpec, ExampleSpec, ExampleSpec, ExampleSpec]
  /** Progressive disclosure: what "View details" reveals, and nothing sooner. */
  detail: string
  suits: string[]
}

export const EXAMPLE_MACHINES: readonly ExampleMachine[] = [
  {
    kind: 'example',
    id: 'standard-s2',
    name: 'Standard S2',
    summary: 'Balanced compute for apps and web services.',
    tone: 'example',
    specs: [
      { value: '2', label: 'vCPU' },
      { value: '4 GB', label: 'RAM' },
      { value: '80 GB', label: 'NVMe SSD' },
      { value: 'Global', label: 'Multiple regions' },
    ],
    detail: 'An everyday shape: enough headroom for a web application and its background work, without paying for cores that idle.',
    suits: ['Web and API services', 'Small databases', 'Staging environments'],
  },
  {
    kind: 'example',
    id: 'compute-c4',
    name: 'Compute C4',
    summary: 'High performance for compute workloads.',
    tone: 'positive',
    specs: [
      { value: '4', label: 'vCPU' },
      { value: '8 GB', label: 'RAM' },
      { value: '160 GB', label: 'NVMe SSD' },
      { value: 'Global', label: 'Multiple regions' },
    ],
    detail: 'Twice the cores of Standard, for work that finishes faster with more of them rather than with more memory.',
    suits: ['Build and CI runners', 'Batch processing', 'Media encoding'],
  },
  {
    kind: 'example',
    id: 'memory-m8',
    name: 'Memory M8',
    summary: 'Optimised for memory-intensive workloads.',
    tone: 'info',
    specs: [
      { value: '8', label: 'vCPU' },
      { value: '32 GB', label: 'RAM' },
      { value: '200 GB', label: 'NVMe SSD' },
      { value: 'Global', label: 'Multiple regions' },
    ],
    detail: 'Four times the memory per core, for datasets that have to stay resident to be fast.',
    suits: ['In-memory caches', 'Analytics queries', 'Large application servers'],
  },
]
